import React, { Component, ErrorInfo, ReactNode } from 'react';
import { errorInterceptor } from '../services/ErrorInterceptor';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
    error?: Error;
}

class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error: Error): State {
        // Update state so the next render will show the fallback UI
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('[ErrorBoundary] Error caught:', error, errorInfo);

        // Check if this is an authentication error
        if (this.isAuthenticationError(error)) {
            console.log('[ErrorBoundary] Authentication error detected, redirecting to login');
            errorInterceptor.handleError(error);
            return;
        }

        // For other errors, just log them
        console.error('[ErrorBoundary] Non-authentication error:', error);
    }

    private isAuthenticationError(error: Error): boolean {
        // Check for authentication-related error messages
        const authErrorMessages = [
            'unauthenticated',
            'unauthorized',
            'authentication failed',
            'invalid token',
            'token expired',
            'session expired',
            'user must be authenticated',
            'failed to load'
        ];

        return authErrorMessages.some(msg =>
            error.message.toLowerCase().includes(msg.toLowerCase())
        );
    }

    render() {
        if (this.state.hasError) {
            // Check if it's an authentication error
            if (this.state.error && this.isAuthenticationError(this.state.error)) {
                // For authentication errors, redirect to login
                errorInterceptor.handleError(this.state.error);
                return null; // Don't render anything while redirecting
            }

            // For other errors, show fallback UI
            if (this.props.fallback) {
                return this.props.fallback;
            }

            return (
                <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                    <div className="max-w-md w-full bg-white dark:bg-gray-800 shadow-lg rounded-lg p-6">
                        <div className="text-center">
                            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900">
                                <svg
                                    className="h-6 w-6 text-red-600 dark:text-red-400"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                                    />
                                </svg>
                            </div>
                            <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
                                Something went wrong
                            </h3>
                            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                                An unexpected error occurred. Please try refreshing the page.
                            </p>
                            <div className="mt-6">
                                <button
                                    onClick={() => window.location.reload()}
                                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                >
                                    Refresh Page
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
